import { useEffect, useState } from "react";

/** Re-render cadence for the relative label — a minute is the finest granularity it shows. */
const TICK_MS = 60_000;

function relativeLabel(at: number, now: number): string {
	const seconds = Math.max(0, Math.round((now - at) / 1000));
	if (seconds < 60) return "just now";
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	return new Date(at).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * When a turn was sent.
 *
 * The thread had no time information at all, so a long session gave no sense of whether an exchange
 * happened a minute or a day ago — the thing you need when returning to previous work. Shown once
 * per turn under the question rather than per message, so it reads as a conversation timestamp
 * instead of chat-log metadata, and it self-updates so "just now" does not go stale on screen.
 *
 * The absolute time lives in the tooltip for anyone who needs it.
 */
export function TurnTimestamp({ at }: { at?: number }) {
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		if (at == null) return;
		const timer = setInterval(() => setNow(Date.now()), TICK_MS);
		return () => clearInterval(timer);
	}, [at]);

	if (at == null) return null;
	return (
		<time
			dateTime={new Date(at).toISOString()}
			title={new Date(at).toLocaleString()}
			className="px-0.5 text-[11px] tabular-nums text-muted-foreground/60"
		>
			{relativeLabel(at, now)}
		</time>
	);
}
